import { useEffect, useState } from "react";
import { CreditCard, Mail, RefreshCw, Trash2, ShieldCheck, ExternalLink } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

const StatusBadge = ({ status, testId }) => {
    const styles = {
        connected: "bg-green-100 text-green-700",
        failed: "bg-red-100 text-red-700",
        action_required: "bg-amber-100 text-amber-800",
    };
    const labels = { connected: "Connected", failed: "Failed", action_required: "Action Required" };
    return (
        <span className={`inline-block rounded-full px-4 py-1.5 text-sm font-bold ${styles[status] || "bg-slate-100 text-slate-600"}`} data-testid={testId}>
            {labels[status] || status}
        </span>
    );
};

const inputCls = "bg-white";

export default function IntegrationsAdmin({ user }) {
    const [config, setConfig] = useState(null);
    const [stripeForm, setStripeForm] = useState({ publishable_key: "", secret_key: "", webhook_secret: "" });
    const [resendForm, setResendForm] = useState({ api_key: "", sender_name: "Freddie's Afro Caribbean Store", sender_email: "", test_to: "" });
    const [busy, setBusy] = useState("");
    const isOwner = user?.role === "owner";

    const load = () => api.get("/admin/integrations/config").then((r) => setConfig(r.data)).catch(() => toast.error("Could not load integration status"));
    useEffect(() => {
        load();
    }, []);

    const run = async (key, fn, successMsg) => {
        setBusy(key);
        try {
            const result = await fn();
            if (result?.message) toast.success(result.message);
            else if (successMsg) toast.success(successMsg);
            setStripeForm({ publishable_key: "", secret_key: "", webhook_secret: "" });
            setResendForm((f) => ({ ...f, api_key: "" }));
            await load();
            return result;
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Action failed"));
            await load();
            return null;
        } finally {
            setBusy("");
        }
    };

    if (!config) return <p className="text-slate-500" data-testid="integrations-loading">Loading integrations…</p>;
    const s = config.stripe;
    const r = config.resend;

    return (
        <div data-testid="admin-integrations" className="space-y-6 max-w-3xl">
            {/* ================= STRIPE ================= */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="integration-stripe">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                    <h2 className="font-heading font-extrabold text-lg text-slate-900 flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-[#0033A0]" /> Stripe Payments
                    </h2>
                    <StatusBadge status={s.status} testId="stripe-status" />
                </div>
                <p className="text-xs text-slate-500 mb-4">
                    Active credentials:{" "}
                    <strong>{s.active_source === "custom" ? "your keys (saved below)" : "platform test sandbox"}</strong>
                    {" · "}{s.mode} mode
                    {s.account_id && <> · account <code className="text-xs">{s.account_id}</code></>}
                </p>
                {s.message && (
                    <p className={`mb-4 rounded-xl px-4 py-3 text-sm font-bold ${s.status === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`} data-testid="stripe-message">
                        {s.message}
                    </p>
                )}
                {s.source === "custom" && (
                    <div className="mb-4 grid sm:grid-cols-3 gap-3 text-sm" data-testid="stripe-current">
                        <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-400">Publishable key</p><p className="font-mono font-bold">{s.publishable_key || "—"}</p></div>
                        <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-400">Secret key</p><p className="font-mono font-bold">{s.secret_key || "—"}</p></div>
                        <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-400">Webhook secret</p><p className="font-mono font-bold">{s.webhook_secret || "—"}</p></div>
                    </div>
                )}
                {s.webhook_url && (
                    <p className="mb-4 text-xs text-slate-500">
                        Webhook endpoint: <code className="break-all">{s.webhook_url}</code>
                        {s.webhook_id && <> — auto-configured (<code>{s.webhook_id}</code>)</>}
                    </p>
                )}

                <div className="space-y-3 border-t border-slate-100 pt-4">
                    <p className="text-sm font-bold text-slate-900">Connect your own Stripe account</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label htmlFor="stripe-pk">Publishable key</Label>
                            <Input id="stripe-pk" className={inputCls} placeholder="pk_test_… or pk_live_…" value={stripeForm.publishable_key}
                                onChange={(e) => setStripeForm({ ...stripeForm, publishable_key: e.target.value })} data-testid="stripe-pk-input" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="stripe-sk">Secret key</Label>
                            <Input id="stripe-sk" type="password" className={inputCls} placeholder="sk_test_… or sk_live_…" value={stripeForm.secret_key}
                                onChange={(e) => setStripeForm({ ...stripeForm, secret_key: e.target.value })} data-testid="stripe-sk-input" autoComplete="off" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="stripe-whsec">Webhook signing secret (optional — we create the webhook for you automatically)</Label>
                        <Input id="stripe-whsec" type="password" className={inputCls} placeholder="whsec_… (leave blank to auto-configure)" value={stripeForm.webhook_secret}
                            onChange={(e) => setStripeForm({ ...stripeForm, webhook_secret: e.target.value })} data-testid="stripe-whsec-input" autoComplete="off" />
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Button
                            disabled={busy !== "" || !stripeForm.publishable_key || !stripeForm.secret_key}
                            onClick={() => run("stripe-connect", () => api.post("/admin/integrations/stripe/connect", stripeForm).then((res) => ({ message: `Connected in ${res.data.mode} mode — webhook configured automatically` })))}
                            className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="stripe-connect">
                            {busy === "stripe-connect" ? "Validating & configuring…" : "Connect & Test"}
                        </Button>
                        <Button variant="outline" disabled={busy !== ""} onClick={() => run("stripe-test", () => api.post("/admin/integrations/stripe/test"))}
                            className="rounded-full" data-testid="stripe-retest">
                            <RefreshCw className={`h-4 w-4 mr-2 ${busy === "stripe-test" ? "animate-spin" : ""}`} /> Re-test Connection
                        </Button>
                        {s.source === "custom" && (
                            <Button variant="outline" disabled={busy !== ""}
                                onClick={() => window.confirm("Remove your keys and revert to the platform test sandbox?") && run("stripe-del", () => api.delete("/admin/integrations/stripe"))}
                                className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="stripe-disconnect">
                                <Trash2 className="h-4 w-4 mr-2" /> Remove Keys
                            </Button>
                        )}
                    </div>
                </div>

                {isOwner && (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="live-approval-panel">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" checked={!!s.live_approved}
                                onChange={(e) => run("live-approval", () => api.post("/admin/integrations/stripe/live-approval", { approved: e.target.checked }),
                                    e.target.checked ? "Live payments approved — live keys can now be connected" : "Live approval withdrawn")}
                                className="mt-1 accent-[#0033A0]" data-testid="live-approval-checkbox" />
                            <span>
                                <span className="block text-sm font-bold text-amber-900">
                                    <ShieldCheck className="inline h-4 w-4 mr-1" /> I approve live payments
                                </span>
                                <span className="block text-xs text-amber-800 mt-0.5">
                                    Only tick this after you have completed Stripe account verification (KYC) and want to take real money. Until then the shop stays in test mode.
                                </span>
                            </span>
                        </label>
                    </div>
                )}

                <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-1.5">
                    <p className="font-bold text-slate-900">Where to find your keys</p>
                    <p>1. Stripe Dashboard → Developers → API keys: <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#0033A0] font-bold hover:underline inline-flex items-center gap-1">dashboard.stripe.com/apikeys <ExternalLink className="h-3 w-3" /></a> (toggle test/live in the dashboard header).</p>
                    <p>2. Paste them above and click Connect & Test — we validate the account, create the webhook endpoint and store everything encrypted. Nothing else to configure.</p>
                    <p>3. Test and live keys are kept separate automatically by their prefix; live payments stay off until you approve them above.</p>
                </div>
            </div>

            {/* ================= RESEND ================= */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="integration-resend">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                    <h2 className="font-heading font-extrabold text-lg text-slate-900 flex items-center gap-2">
                        <Mail className="h-5 w-5 text-[#0033A0]" /> Resend Email
                    </h2>
                    <StatusBadge status={r.status} testId="resend-status" />
                </div>
                <p className="text-xs text-slate-500 mb-4">
                    Active sender: <strong>{r.active_source === "direct" ? `${r.sender_name} <${r.sender_email}>` : "platform managed sender (onboarding@resend.dev)"}</strong>.
                    Used for account verification, password resets, order confirmations, payment receipts, order updates, collection notifications and refunds.
                </p>
                {r.message && (
                    <p className={`mb-4 rounded-xl px-4 py-3 text-sm font-bold ${r.status === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`} data-testid="resend-message">
                        {r.message}
                    </p>
                )}
                {r.source === "custom" && (
                    <p className="mb-4 text-sm rounded-xl bg-slate-50 p-3" data-testid="resend-current">
                        <span className="text-xs text-slate-400 block">API key</span>
                        <span className="font-mono font-bold">{r.api_key}</span>
                    </p>
                )}

                <div className="space-y-3 border-t border-slate-100 pt-4">
                    <p className="text-sm font-bold text-slate-900">Connect your own Resend account</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label htmlFor="resend-key">API key</Label>
                            <Input id="resend-key" type="password" className={inputCls} placeholder="re_…" value={resendForm.api_key}
                                onChange={(e) => setResendForm({ ...resendForm, api_key: e.target.value })} data-testid="resend-key-input" autoComplete="off" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="resend-name">Sender name</Label>
                            <Input id="resend-name" className={inputCls} value={resendForm.sender_name}
                                onChange={(e) => setResendForm({ ...resendForm, sender_name: e.target.value })} data-testid="resend-name-input" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="resend-email">Sender email</Label>
                            <Input id="resend-email" type="email" className={inputCls} placeholder="orders@yourdomain.co.uk" value={resendForm.sender_email}
                                onChange={(e) => setResendForm({ ...resendForm, sender_email: e.target.value })} data-testid="resend-email-input" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="resend-test-to">Send test email to</Label>
                            <Input id="resend-test-to" type="email" className={inputCls} placeholder="you@example.com" value={resendForm.test_to}
                                onChange={(e) => setResendForm({ ...resendForm, test_to: e.target.value })} data-testid="resend-test-to-input" />
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Button
                            disabled={busy !== "" || !resendForm.api_key || !resendForm.sender_email || !resendForm.test_to}
                            onClick={() => run("resend-connect", () => api.post("/admin/integrations/resend/connect", resendForm).then((res) => ({ message: res.data.message || `Status: ${res.data.status}` })))}
                            className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="resend-connect">
                            {busy === "resend-connect" ? "Validating & sending test…" : "Connect & Test"}
                        </Button>
                        {r.source === "custom" && (
                            <Button variant="outline" disabled={busy !== ""}
                                onClick={() => {
                                    const to = resendForm.test_to || user.email;
                                    run("resend-test", () => api.post("/admin/integrations/resend/test", { to }));
                                }}
                                className="rounded-full" data-testid="resend-retest">
                                <RefreshCw className={`h-4 w-4 mr-2 ${busy === "resend-test" ? "animate-spin" : ""}`} /> Send Test Again
                            </Button>
                        )}
                        {r.source === "custom" && (
                            <Button variant="outline" disabled={busy !== ""}
                                onClick={() => window.confirm("Remove your Resend key and revert to the platform sender?") && run("resend-del", () => api.delete("/admin/integrations/resend"))}
                                className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="resend-disconnect">
                                <Trash2 className="h-4 w-4 mr-2" /> Remove Key
                            </Button>
                        )}
                    </div>
                </div>

                <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-1.5">
                    <p className="font-bold text-slate-900">Setup guide</p>
                    <p>1. Create an API key at <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#0033A0] font-bold hover:underline inline-flex items-center gap-1">resend.com/api-keys <ExternalLink className="h-3 w-3" /></a>.</p>
                    <p>2. To send from your own address (e.g. orders@yourdomain.co.uk), verify the domain first at <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-[#0033A0] font-bold hover:underline inline-flex items-center gap-1">resend.com/domains <ExternalLink className="h-3 w-3" /></a> — Resend shows you the DNS records to add. Until the domain is verified, the test email will fail with an "Action Required" message here.</p>
                    <p>3. Connect & Test validates the key, saves it encrypted and sends a real branded test email. If anything needs attention, the status above tells you exactly what.</p>
                    <p>4. With no custom key saved, emails send via the platform's managed sender — nothing to do.</p>
                </div>
            </div>
        </div>
    );
}
